import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Icon } from '@garnish/core';

import { useTelemetry } from '@order/Telemetry';

import { locationResultsListsV2Messages as messages } from './LocationResultsListsV2ViewMap.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2ViewMap = (props: LocationResultsListsV2ViewMapProps) => {
  const { onPress } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.();

    track('location.mapview.tapped');
  }, [onPress, track]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [styles.container, styles.containerUpdate];

  return (
    <TouchableOpacity
      activeOpacity={ACTIVE_OPACITY}
      accessibilityLabel={formatMessage(messages.viewMapButton)}
      accessibilityRole="button"
      style={containerStyles}
      onPress={handleOnPress}
    >
      <Icon
        name="IconPickupStrokeLg"
        color={theme.colors.DARK_KALE}
        width={VIEW_MAP_ICON_SIZE}
        height={VIEW_MAP_ICON_SIZE}
      />

      <BodyText sizeMatch={['14']} style={styles.label}>
        <FormattedMessage {...messages.viewMapButton} />
      </BodyText>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTIVE_OPACITY = 0.8;
const VIEW_MAP_ICON_SIZE = 20;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['1'],
    borderRadius: theme.radius.xlarge,
    minHeight: 40,
    paddingVertical: theme.spacing[1],
    paddingHorizontal: theme.spacing[4],
  },
  containerUpdate: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  label: {
    color: theme.colors.DARK_KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListsV2ViewMapProps = {
  onPress?: () => void;
};
